/**
 * Main App Component
 *
 * Sets up:
 * - React Router
 * - Auth Provider
 * - Protected Routes
 * - Error Boundary
 * - Toast Notifications
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Navbar } from './components/Navbar';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import { ProtectedTenantAdminRoute } from './components/ProtectedTenantAdminRoute';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Workflows } from './pages/Workflows';
import { Analytics } from './pages/Analytics';
import { Settings } from './pages/Settings';
import { AdminMetrics } from './pages/AdminMetrics';
import { AdminWorkflowRegistry } from './pages/AdminWorkflowRegistry';

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Public Route Component
 * Redirects to dashboard if already authenticated
 */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Only redirect if user is actually authenticated (has both user and token)
  if (isAuthenticated && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * Layout Component with Navbar
 */
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-4">
        {children}
      </main>
    </div>
  );
};

/**
 * Route Configuration Type
 */
type RouteConfig = {
  path: string;
  component: React.FC;
};

/**
 * Helper function to create protected routes
 */
const createProtectedRoute = (config: RouteConfig) => (
  <Route
    key={config.path}
    path={config.path}
    element={
      <ProtectedRoute>
        <Layout>
          <config.component />
        </Layout>
      </ProtectedRoute>
    }
  />
);

/**
 * Protected Routes Configuration
 */
const protectedRoutes: RouteConfig[] = [
  { path: '/dashboard', component: Dashboard },
  { path: '/workflows', component: Workflows },
  { path: '/analytics', component: Analytics },
  { path: '/settings', component: Settings },
];

/**
 * App Routes
 */
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      {protectedRoutes.map(createProtectedRoute)}

      {/* Admin Routes (Platform Admin Only) */}
      <Route
        path="/admin/metrics"
        element={
          <ProtectedRoute>
            <ProtectedAdminRoute>
              <Layout>
                <AdminMetrics />
              </Layout>
            </ProtectedAdminRoute>
          </ProtectedRoute>
        }
      />
      {/* Workflow Registry (All Tenant Admins) */}
      <Route
        path="/admin/workflow-registry"
        element={
          <ProtectedRoute>
            <ProtectedTenantAdminRoute>
              <Layout>
                <AdminWorkflowRegistry />
              </Layout>
            </ProtectedTenantAdminRoute>
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

/**
 * Main App Component
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <AppRoutes />
          <Toaster
            toastOptions={{
              style: {
                background: '#171717',
                color: '#ffffff',
                border: '1px solid #2C2C2C',
                borderRadius: '0.75rem',
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;


